import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './components/Login';
import SignUp from './components/SignUp';
import SimpleTimePicker from './components/SimpleTimePicker';
import AccountantDashboard from './components/AccountantDashboard';
import PartTimerMasterManagement from './components/PartTimerMasterManagement';
import PartTimerManualEntry from './components/PartTimerManualEntry';
import RegularEmployeeManagement from './components/RegularEmployeeManagement';
import RegularEmployeeBulkEntry from './components/RegularEmployeeBulkEntry';
import HolidayManagement from './components/HolidayManagement';
import { auth } from './firebase/config';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import {
  getUserAttendanceRecords,
  getAllAttendanceRecords,
  getAttendanceRecordByDate,
  addAttendanceRecord,
  updateAttendanceRecord,
  deleteAttendanceRecord,
  updateUserInfo,
  getUserInfo,
  getHolidayByDate,
  BUSINESS_ENTITIES
} from './firebase/firestore';
import { cleanupDuplicateRecords } from './firebase/cleanupDuplicates';
import { ACCOUNTANT_EMAILS } from './config/roleConfig';
// import { runTestScenarios } from './utils/salaryValidator'; // 테스트용 - 필요시 주석 해제

function App() {
  // 사용자 정보 및 로그인 상태
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdminView, setIsAdminView] = useState(false);
  const [isAccountantView, setIsAccountantView] = useState(false);
  const [showSignUp, setShowSignUp] = useState(false);
  const [activeTab, setActiveTab] = useState('attendance'); // attendance, salary, accountant
  
  // 출퇴근 기록
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [timeInput, setTimeInput] = useState({ checkIn: '', checkOut: '' });
  
  // 설정
  const [deductLunch, setDeductLunch] = useState(true);
  const [hourlyWage, setHourlyWage] = useState(10000);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const handlePeriodChange = useCallback(({ year, month }) => {
    let nextYear = typeof year === 'number' && !Number.isNaN(year) ? year : selectedYear;
    let nextMonth = typeof month === 'number' && !Number.isNaN(month) ? month : selectedMonth;

    if (typeof nextMonth === 'number') {
      const normalized = new Date(nextYear, nextMonth, 1);
      nextYear = normalized.getFullYear();
      nextMonth = normalized.getMonth();
    }

    if (nextYear !== selectedYear) {
      setSelectedYear(nextYear);
    }
    if (nextMonth !== selectedMonth) {
      setSelectedMonth(nextMonth);
    }
  }, [selectedMonth, selectedYear]);

  const renderPeriodSelectors = (wrapperClass = '') => (
    <div className={`mb-6 ${wrapperClass}`.trim()}>
      <div className="flex gap-4 items-center bg-white rounded-lg p-4 shadow">
        <label className="font-medium">정산 년월:</label>
        <select
          value={selectedYear}
          onChange={(e) => handlePeriodChange({ year: Number(e.target.value), month: selectedMonth })}
          className="px-4 py-2 border rounded-lg"
        >
          {Array.from({ length: 7 }, (_, idx) => selectedYear - 3 + idx).map((year) => (
            <option key={year} value={year}>{year}년</option>
          ))}
        </select>
        <select
          value={selectedMonth}
          onChange={(e) => handlePeriodChange({ year: selectedYear, month: Number(e.target.value) })}
          className="px-4 py-2 border rounded-lg"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i}>{i + 1}월</option>
          ))}
        </select>
      </div>
    </div>
  );

  // 사용자 목록 (관리자용)
  
  // 사업자 선택
  const [selectedBusinessEntity, setSelectedBusinessEntity] = useState(
    localStorage.getItem('selectedBusinessEntity') || BUSINESS_ENTITIES[0].id
  );

  // 출퇴근 기록 불러오기 (Firestore 사용)
  const loadAttendanceRecords = useCallback(async (userId, isAdmin = false) => {
    try {
      console.log('loadAttendanceRecords 호출:', { userId, isAdmin });
      const result = isAdmin 
        ? await getAllAttendanceRecords()
        : await getUserAttendanceRecords(userId);
      
      if (result.success) {
        console.log('출퇴근 기록 로드 성공:', result.records.length, '개 기록');
        setAttendanceRecords(result.records);
      } else {
        console.error('출퇴근 기록 로드 실패:', result.error);
      }
    } catch (error) {
      console.error('출퇴근 기록 로드 오류:', error);
    }
  }, []);

  // Firebase Auth 상태 감지
  const isAccountantEmail = (email) => ACCOUNTANT_EMAILS.includes((email || '').toLowerCase());

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userInfo = await getUserInfo(user.uid);
        if (userInfo.success) {
          let userData = { ...userInfo.user, uid: user.uid, id: user.uid };
          const accountantLogin = isAccountantEmail(userData.email);

          if (accountantLogin && userInfo.user.role !== 'accountant') {
            await updateUserInfo(user.uid, { role: 'accountant' });
            userData = { ...userData, role: 'accountant' };
            userInfo.user.role = 'accountant';
          }

          setCurrentUser(userData);
          setIsLoggedIn(true);
          setHourlyWage(userData.hourlyWage);
          console.log('Firebase Auth: 사용자 정보 설정:', userData);
          
          // 중복 기록 정리
          cleanupDuplicateRecords(user.uid).then(result => {
            if (result.deletedCount > 0) {
              console.log(`${result.deletedCount}개의 중복 기록이 정리되었습니다.`);
            }
          });
          
          loadAttendanceRecords(user.uid, userInfo.user.isAdmin || userInfo.user.role === 'accountant');

          // 사용자 역할에 따른 뷰 설정
          if (accountantLogin || userInfo.user.role === 'accountant') {
            setIsAccountantView(true);
            setIsAdminView(false);
            setActiveTab('accountant');
          } else if (userInfo.user.isAdmin) {
            setIsAdminView(true);
            setIsAccountantView(false);
            setActiveTab('parttimer-salary');
          } else {
            setIsAdminView(false);
            setIsAccountantView(false);
            setActiveTab('attendance');
          }
        }
      } else {
        setIsLoggedIn(false);
        setCurrentUser(null);
      }
    });

    return () => unsubscribe();
  }, [loadAttendanceRecords]);

  // 사업자 변경 시 localStorage에 저장
  useEffect(() => {
    localStorage.setItem('selectedBusinessEntity', selectedBusinessEntity);
  }, [selectedBusinessEntity]);


  // 로그아웃 처리
  const handleLogout = async () => {
    try {
      await signOut(auth);
      setIsLoggedIn(false);
      setCurrentUser(null);
      setIsAdminView(false);
      setAttendanceRecords([]);
    } catch (error) {
      console.error('로그아웃 오류:', error);
    }
  };

  // 선택된 날짜의 기록 가져오기
  const getSelectedDateRecord = () => {
    return attendanceRecords.find(r => r.date === selectedDate);
  };

  // 출퇴근 기록은 이제 Firestore에 직접 저장되므로 이 함수는 더 이상 필요하지 않음
  // 대신 각 작업(추가/수정/삭제)에서 직접 Firestore API 호출

  // 출근 시간 입력 처리
  const handleCheckIn = async () => {
    if (!timeInput.checkIn) {
      alert('출근 시간을 입력해주세요.');
      return;
    }
    
    console.log('출근 처리 시작:', { 
      selectedDate, 
      checkIn: timeInput.checkIn, 
      currentUser: currentUser,
      attendanceRecords: attendanceRecords.length 
    });
    
    try {
      const userId = currentUser.uid || currentUser.id;
      console.log('사용자 ID:', userId);
      
      // Firestore에서 직접 해당 날짜의 기록을 조회
      const { success, record: existingRecord, error } = await getAttendanceRecordByDate(userId, selectedDate);
      
      if (!success) {
        console.error('기록 조회 실패:', error);
        alert('기록 조회 중 오류가 발생했습니다.');
        return;
      }
      
      console.log('기존 기록:', existingRecord);
      
      if (existingRecord) {
        // 기존 기록 업데이트
        const updatedData = {
          checkIn: timeInput.checkIn,
          workHours: existingRecord.checkOut 
            ? calculateWorkHours(timeInput.checkIn, existingRecord.checkOut)
            : 0,
          lunchDeducted: deductLunch
        };
        
        console.log('업데이트 데이터:', updatedData);
        const updateResult = await updateAttendanceRecord(existingRecord.id, updatedData);
        console.log('업데이트 결과:', updateResult);
        
        if (updateResult.success) {
          await loadAttendanceRecords(userId, currentUser.isAdmin);
          alert('출근 시간이 기록되었습니다.');
        } else {
          alert('출근 시간 업데이트 실패: ' + updateResult.error);
        }
      } else {
        // 새로운 기록 생성
        // 공휴일 체크
        const holidayResult = await getHolidayByDate(selectedDate);
        const isHoliday = holidayResult.success && holidayResult.holiday !== null;

        const newRecord = {
          date: selectedDate,
          checkIn: timeInput.checkIn,
          checkOut: null,
          workHours: 0,
          lunchDeducted: deductLunch,
          userName: currentUser.name,
          isHoliday: isHoliday
        };

        console.log('새 기록 데이터:', newRecord);
        const addResult = await addAttendanceRecord(userId, newRecord);
        console.log('추가 결과:', addResult);

        if (addResult.success) {
          await loadAttendanceRecords(userId, currentUser.isAdmin);
          const message = isHoliday
            ? `출근 시간이 기록되었습니다.\n🎊 공휴일 근무입니다 (수당 1.5배 적용)`
            : '출근 시간이 기록되었습니다.';
          alert(message);
        } else {
          alert('출근 시간 기록 실패: ' + addResult.error);
        }
      }
      
      setTimeInput({ ...timeInput, checkIn: '' });
    } catch (error) {
      console.error('출근 처리 오류:', error);
      alert('출근 처리 중 오류가 발생했습니다.');
    }
  };

  // 퇴근 시간 입력 처리
  const handleCheckOut = async () => {
    if (!timeInput.checkOut) {
      alert('퇴근 시간을 입력해주세요.');
      return;
    }
    
    try {
      const userId = currentUser.uid || currentUser.id;
      
      // Firestore에서 직접 해당 날짜의 기록을 조회
      const { success, record: existingRecord, error } = await getAttendanceRecordByDate(userId, selectedDate);
      
      if (!success) {
        console.error('기록 조회 실패:', error);
        alert('기록 조회 중 오류가 발생했습니다.');
        return;
      }
      
      if (!existingRecord || !existingRecord.checkIn) {
        alert('먼저 출근 시간을 입력해주세요.');
        return;
      }
      
      const updatedData = {
        checkOut: timeInput.checkOut,
        workHours: calculateWorkHours(existingRecord.checkIn, timeInput.checkOut),
        lunchDeducted: deductLunch
      };
      
      const updateResult = await updateAttendanceRecord(existingRecord.id, updatedData);
      
      if (updateResult.success) {
        await loadAttendanceRecords(userId, currentUser.isAdmin);
        alert('퇴근 시간이 기록되었습니다.');
      } else {
        alert('퇴근 시간 업데이트 실패: ' + updateResult.error);
      }
      
      setTimeInput({ ...timeInput, checkOut: '' });
    } catch (error) {
      console.error('퇴근 처리 오류:', error);
      alert('퇴근 처리 중 오류가 발생했습니다.');
    }
  };

  // 근무시간 계산
  const calculateWorkHours = (checkIn, checkOut) => {
    const [inHour, inMin] = checkIn.split(':').map(Number);
    const [outHour, outMin] = checkOut.split(':').map(Number);
    
    // 출근시간 30분 단위 올림 처리
    // 예: 8:31 -> 9:00, 8:50 -> 9:00, 9:01 -> 9:30
    let adjustedInHour = inHour;
    let adjustedInMin = 0;
    
    if (inMin > 0 && inMin <= 30) {
      adjustedInMin = 30;
    } else if (inMin > 30) {
      adjustedInHour += 1;
      adjustedInMin = 0;
    }
    
    // 퇴근시간 30분 단위 내림 처리
    // 예: 18:20 -> 18:00, 18:50 -> 18:30
    let adjustedOutHour = outHour;
    let adjustedOutMin = 0;
    
    if (outMin >= 30) {
      adjustedOutMin = 30;
    }
    
    // 조정된 시간으로 총 근무시간 계산
    let totalMinutes = (adjustedOutHour * 60 + adjustedOutMin) - (adjustedInHour * 60 + adjustedInMin);
    
    // 점심시간 공제 (1시간)
    if (deductLunch && totalMinutes > 240) { // 4시간 이상 근무시
      totalMinutes -= 60;
    }
    
    // 시간으로 변환
    const hours = totalMinutes / 60;
    
    return Math.max(0, hours);
  };

  // 주휴수당 계산
  const calculateWeeklyAllowance = (year = selectedYear, month = selectedMonth) => {
    // 주별로 그룹화 (일요일~토요일)
    const weeklyData = {};
    
    attendanceRecords.forEach(record => {
      const date = new Date(record.date);
      if (date.getMonth() === month && date.getFullYear() === year) {
        // 주의 시작일(일요일) 계산
        const dayOfWeek = date.getDay();
        const weekStart = new Date(date);
        weekStart.setDate(date.getDate() - dayOfWeek);
        const weekKey = weekStart.toISOString().split('T')[0];
        
        if (!weeklyData[weekKey]) {
          weeklyData[weekKey] = {
            hours: 0,
            dates: [],
            startDate: weekStart,
            endDate: new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)
          };
        }
        
        weeklyData[weekKey].hours += record.workHours;
        weeklyData[weekKey].dates.push(record.date);
      }
    });
    
    // 주 15시간 이상 근무한 주 계산
    // 주휴수당 = (주 근무시간 / 주 소정근무시간) × 8 × 시급
    // 주 소정근무시간을 40시간으로 설정 (일반적인 주 5일 8시간 근무)
    const weeklyStandardHours = 40;
    let weeklyAllowanceHours = 0;
    const eligibleWeeks = [];
    
    Object.entries(weeklyData).forEach(([weekKey, data]) => {
      if (data.hours >= 15) {
        // 비례 계산: (실제 근무시간 / 주 소정근무시간) × 8시간
        const proportionalHours = (data.hours / weeklyStandardHours) * 8;
        // 최대 8시간으로 제한 (주 40시간 이상 근무해도 8시간만 지급)
        const allowanceHours = Math.min(proportionalHours, 8);
        
        weeklyAllowanceHours += allowanceHours;
        eligibleWeeks.push({
          weekStart: data.startDate,
          weekEnd: data.endDate,
          dates: data.dates,
          totalHours: data.hours,
          allowanceHours: allowanceHours,
          allowanceAmount: allowanceHours * hourlyWage
        });
      }
    });
    
    return { hours: weeklyAllowanceHours, eligibleWeeks };
  };

  // 급여 계산
  const calculateSalary = (year = selectedYear, month = selectedMonth) => {
    // 선택된 달의 총 근무시간
    const totalHours = attendanceRecords
      .filter(r => {
        const date = new Date(r.date);
        return date.getMonth() === month && date.getFullYear() === year;
      })
      .reduce((sum, r) => sum + r.workHours, 0);
    
    // 주휴수당 계산
    const weeklyAllowanceData = calculateWeeklyAllowance(year, month);
    const weeklyAllowanceHours = weeklyAllowanceData.hours;
    
    // 기본급
    const basicPay = totalHours * hourlyWage;
    
    // 주휴수당
    const weeklyAllowance = weeklyAllowanceHours * hourlyWage;
    
    // 총급여
    const totalPay = basicPay + weeklyAllowance;
    
    // 소득세 (3.3%)
    const tax = totalPay * 0.033;
    
    // 실지급액
    const netPay = totalPay - tax;
    
    return {
      totalHours,
      weeklyAllowanceHours,
      basicPay,
      weeklyAllowance,
      totalPay,
      tax,
      netPay,
      eligibleWeeks: weeklyAllowanceData.eligibleWeeks
    };
  };

  // 특정 월의 기록 필터링
  const getMonthRecords = (year, month) => {
    return attendanceRecords.filter(r => {
      const date = new Date(r.date);
      return date.getMonth() === month && date.getFullYear() === year;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  // 로그인 화면
  if (!isLoggedIn) {
    if (showSignUp) {
      return <SignUp onSignUpSuccess={() => setShowSignUp(false)} />;
    }
    return <Login onSuccess={async (userData) => {
      const normalizedEmail = (userData.email || '').toLowerCase();
      const accountantLogin = isAccountantEmail(normalizedEmail);
      const targetUid = userData.uid || userData.id;

      if (accountantLogin && userData.role !== 'accountant') {
        await updateUserInfo(targetUid, { role: 'accountant' });
        userData.role = 'accountant';
      }

      const userWithUid = {
        ...userData,
        uid: targetUid,
        role: accountantLogin ? 'accountant' : userData.role
      };

      setCurrentUser(userWithUid);
      setIsLoggedIn(true);
      setHourlyWage(userData.hourlyWage || 10000);

      // 사용자 역할에 따른 뷰 설정
      if (accountantLogin || userData.role === 'accountant') {
        setIsAccountantView(true);
        setIsAdminView(false);
        setActiveTab('accountant');
      } else if (userData.isAdmin) {
        setIsAdminView(true);
        setIsAccountantView(false);
        setActiveTab('salary');
      } else {
        setIsAdminView(false);
        setIsAccountantView(false);
        setActiveTab('attendance');
      }
      console.log('Login onSuccess: 사용자 정보:', userWithUid);

      // 중복 기록 정리
      cleanupDuplicateRecords(userWithUid.uid).then(result => {
        if (result.deletedCount > 0) {
          console.log(`${result.deletedCount}개의 중복 기록이 정리되었습니다.`);
        }
      });

      loadAttendanceRecords(userWithUid.uid, userData.isAdmin || userWithUid.role === 'accountant');
    }} onSignUpClick={() => setShowSignUp(true)} />;
  }

  const salary = calculateSalary();
  const monthRecords = getMonthRecords(selectedYear, selectedMonth);

  // 세무사 전용 화면
  if (isLoggedIn && isAccountantView && currentUser.role === 'accountant') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="container mx-auto px-4 py-8">
          <header className="mb-10">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-3xl font-bold text-gray-800 mb-2">월별 급여 기초 자료</h1>
                  <p className="text-gray-600">{currentUser.name}님 (세무사)</p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleLogout}
                    className="px-5 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    로그아웃
                  </button>
                </div>
              </div>
            </div>
          </header>

          {renderPeriodSelectors()}

          <AccountantDashboard
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            businessEntity={selectedBusinessEntity}
          />
        </div>
      </div>
    );
  }

  // 관리자 화면
  if (isLoggedIn && isAdminView && currentUser.isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
        <div className="container mx-auto px-4 py-8">
          <header className="mb-10" style={{ animation: 'slideInFromTop 0.5s ease-out' }}>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                <div>
                  <h1 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2 flex items-center flex-wrap">
                    <svg className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span>관리자 대시보드</span>
                  </h1>
                  <p className="text-sm sm:text-base text-gray-600 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {currentUser.name}님이 로그인 중
                  </p>
                  {/* 사업자 선택 드롭다운 */}
                  <div className="mt-2">
                    <select
                      value={selectedBusinessEntity}
                      onChange={(e) => setSelectedBusinessEntity(e.target.value)}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {BUSINESS_ENTITIES.map(entity => (
                        <option key={entity.id} value={entity.id}>
                          {entity.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={() => setIsAdminView(false)}
                    className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transform transition-all duration-200 hover:scale-105 shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span className="hidden sm:inline">내 근무현황</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transform transition-all duration-200 hover:scale-105 shadow-md"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    로그아웃
                  </button>
                </div>
              </div>
            </div>
          </header>

          {/* 탭 네비게이션 */}
          <div className="mb-6">
            <div className="bg-white rounded-lg shadow p-2 flex gap-2 flex-wrap">
              <button
                onClick={() => setActiveTab('parttimer-master')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'parttimer-master'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                파트타이머 마스터
              </button>
              <button
                onClick={() => setActiveTab('parttimer-manual')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'parttimer-manual'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                파트타이머 수기입력
              </button>
              <button
                onClick={() => setActiveTab('regular-master')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'regular-master'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                정규직원 마스터
              </button>
              <button
                onClick={() => setActiveTab('regular-bulk')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'regular-bulk'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                정규직원 급여입력
              </button>
              <button
                onClick={() => setActiveTab('accountant-view')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'accountant-view'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                세무 보기
              </button>
              <button
                onClick={() => setActiveTab('holiday-management')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  activeTab === 'holiday-management'
                    ? 'bg-purple-600 text-white'
                    : 'hover:bg-gray-100'
                }`}
              >
                공휴일 관리
              </button>
            </div>
          </div>

          {/* 파트타이머 마스터 관리 */}
          {activeTab === 'parttimer-master' && (
            <PartTimerMasterManagement
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              onChangePeriod={handlePeriodChange}
            />
          )}

          {/* 정규직원 마스터 데이터 관리 */}
          {activeTab === 'regular-master' && (
            <RegularEmployeeManagement />
          )}

          {/* 정규직원 급여 일괄입력 */}
          {activeTab === 'regular-bulk' && (
            <RegularEmployeeBulkEntry
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              businessEntity={selectedBusinessEntity}
              currentUser={currentUser}
              onChangePeriod={handlePeriodChange}
            />
          )}

          {/* 파트타이머 수기 입력 */}
          {activeTab === 'parttimer-manual' && (
            <PartTimerManualEntry
              selectedMonth={selectedMonth}
              selectedYear={selectedYear}
              businessEntity={selectedBusinessEntity}
              onChangePeriod={handlePeriodChange}
            />
          )}

          {/* 세무사 뷰 (관리자용) */}
          {activeTab === 'accountant-view' && (
            <>
              {renderPeriodSelectors()}
              <AccountantDashboard
                selectedMonth={selectedMonth}
                selectedYear={selectedYear}
                businessEntity={selectedBusinessEntity}
              />
            </>
          )}

          {/* 공휴일 관리 */}
          {activeTab === 'holiday-management' && (
            <HolidayManagement />
          )}
        </div>
      </div>
    );
  }

  // 메인 화면
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <header className="mb-10" style={{ animation: 'slideInFromTop 0.5s ease-out' }}>
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2 flex items-center flex-wrap">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>급여관리 시스템</span>
                </h1>
                <p className="text-sm sm:text-base text-gray-600 flex items-center">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {currentUser.name}님, 좋은 하루 되세요! 👋
                </p>
              </div>
              <div className="flex gap-2 sm:gap-3">
                {currentUser.isAdmin && (
                  <button
                    onClick={() => setIsAdminView(true)}
                    className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transform transition-all duration-200 hover:scale-105 shadow-md text-sm sm:text-base"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="hidden sm:inline">관리자 페이지</span>
                  </button>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transform transition-all duration-200 hover:scale-105 shadow-md text-sm sm:text-base"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <span className="hidden sm:inline">로그아웃</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* 출퇴근 시간 입력 */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 card-hover" style={{ animation: 'fadeIn 0.6s ease-out' }}>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 mr-2 sm:mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              근무시간 등록
            </h2>
            <div className="flex items-center gap-3 bg-gray-100 rounded-lg px-4 py-2">
              <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="bg-transparent outline-none text-gray-700 font-medium text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6">
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                <svg className="w-5 h-5 mr-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                </svg>
                출근 시간
              </label>
              <div className="flex gap-3">
                <SimpleTimePicker
                  value={timeInput.checkIn}
                  onChange={(time) => setTimeInput({ ...timeInput, checkIn: time })}
                  placeholder="출근 시간 선택"
                />
                <button
                  onClick={handleCheckIn}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transform transition-all duration-200 hover:scale-105 shadow-md font-medium"
                >
                  기록
                </button>
              </div>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl p-6">
              <label className="flex items-center text-sm font-semibold text-gray-700 mb-3">
                <svg className="w-5 h-5 mr-2 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                퇴근 시간
              </label>
              <div className="flex gap-3">
                <SimpleTimePicker
                  value={timeInput.checkOut}
                  onChange={(time) => setTimeInput({ ...timeInput, checkOut: time })}
                  placeholder="퇴근 시간 선택"
                />
                <button
                  onClick={handleCheckOut}
                  disabled={!getSelectedDateRecord() || !getSelectedDateRecord()?.checkIn}
                  className={`px-6 py-3 rounded-lg font-medium transform transition-all duration-200 shadow-md ${
                    !getSelectedDateRecord() || !getSelectedDateRecord()?.checkIn
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 hover:scale-105'
                  }`}
                >
                  기록
                </button>
              </div>
            </div>
          </div>
          
          <div className="mt-6">
            {(() => {
              const selectedRecord = getSelectedDateRecord();
              const isToday = selectedDate === new Date().toISOString().split('T')[0];
              
              return selectedRecord ? (
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 text-center">
                  <p className="text-sm font-medium text-gray-600 mb-2">
                    {isToday ? '📅 오늘' : '📆 ' + new Date(selectedDate).toLocaleDateString('ko-KR')} 기록
                  </p>
                  <div className="flex justify-center items-center gap-4 text-lg">
                    {selectedRecord.checkIn && (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        출근: <span className="font-semibold">{selectedRecord.checkIn}</span>
                      </span>
                    )}
                    {selectedRecord.checkOut && (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        퇴근: <span className="font-semibold">{selectedRecord.checkOut}</span>
                      </span>
                    )}
                  </div>
                  {selectedRecord.workHours > 0 && (
                    <p className="mt-3 text-xl font-bold text-gray-800">
                      근무시간: {selectedRecord.workHours.toFixed(1)}시간
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-gray-100 rounded-xl p-4 text-center">
                  <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500">
                    {selectedDate === new Date().toISOString().split('T')[0] ? '오늘' : new Date(selectedDate).toLocaleDateString('ko-KR')} 출퇴근 기록이 없습니다.
                  </p>
                </div>
              );
            })()}
          </div>
          
          <div className="mt-6 bg-yellow-50 rounded-xl p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={deductLunch}
                onChange={(e) => setDeductLunch(e.target.checked)}
                className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
              />
              <span className="ml-3 text-gray-700 font-medium">🍴 점심시간 1시간 공제</span>
            </label>
            <div className="text-xs text-gray-600 mt-2 ml-8 space-y-1">
              <p className="font-semibold">⚠️ 근무시간 계산 방식:</p>
              <p>• 출근: 30분 단위 올림 (8:31→9:00)</p>
              <p>• 퇴근: 30분 단위 내림 (18:20→18:00)</p>
            </div>
          </div>
        </div>

        {/* 근무 기록표 */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 card-hover" style={{ animation: 'fadeIn 0.7s ease-out' }}>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <svg className="w-6 h-6 mr-3 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              근무 기록
            </h2>
            <div className="flex items-center gap-3">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {[2024, 2025, 2026].map(year => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {[
                  '월', '월', '월', '월', '월', '월',
                  '월', '월', '월', '월', '월', '월'
                ].map((month, index) => (
                  <option key={index} value={index}>{index + 1}{month}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-200">
            <table className="min-w-full">
              <thead className="bg-gradient-to-r from-purple-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">날짜</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">출근</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">퇴근</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">근무시간</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">관리</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {monthRecords.map((record, index) => (
                  <tr key={index} className="table-row-hover hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(record.date).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric', weekday: 'short' })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-medium">
                        {record.checkIn ? (
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            {record.checkIn}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 font-medium">
                        {record.checkOut ? (
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            {record.checkOut}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {record.workHours ? (
                        <div className="group relative inline-block">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {record.workHours.toFixed(1)}시간
                          </span>
                          {/* 조정된 시간 툴팁 */}
                          {record.checkIn && record.checkOut && (
                            <div className="absolute z-10 invisible group-hover:visible bg-gray-900 text-white text-xs rounded-lg py-2 px-3 -top-14 left-1/2 transform -translate-x-1/2 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                              <div className="text-left">
                                {(() => {
                                  const [inHour, inMin] = record.checkIn.split(':').map(Number);
                                  const [outHour, outMin] = record.checkOut.split(':').map(Number);
                                  
                                  // 출근시간 조정
                                  let adjustedInHour = inHour;
                                  let adjustedInMin = 0;
                                  if (inMin > 0 && inMin <= 30) adjustedInMin = 30;
                                  else if (inMin > 30) { adjustedInHour += 1; adjustedInMin = 0; }
                                  
                                  // 퇴근시간 조정
                                  let adjustedOutMin = outMin >= 30 ? 30 : 0;
                                  
                                  const adjustedIn = `${String(adjustedInHour).padStart(2, '0')}:${String(adjustedInMin).padStart(2, '0')}`;
                                  const adjustedOut = `${String(outHour).padStart(2, '0')}:${String(adjustedOutMin).padStart(2, '0')}`;
                                  
                                  return (
                                    <>
                                      <div className="mb-1">실제 시간 → 계산 시간</div>
                                      <div>출근: {record.checkIn} → {adjustedIn}</div>
                                      <div>퇴근: {record.checkOut} → {adjustedOut}</div>
                                    </>
                                  );
                                })()}
                              </div>
                              <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => {
                          setSelectedDate(record.date);
                          setTimeInput({
                            checkIn: record.checkIn || '',
                            checkOut: record.checkOut || ''
                          });
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-sm font-medium rounded-lg text-blue-700 bg-white hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 mr-2"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        수정
                      </button>
                      <button
                        onClick={async () => {
                          if (window.confirm('이 기록을 삭제하시겠습니까?')) {
                            try {
                              await deleteAttendanceRecord(record.id);
                              const userId = currentUser.uid || currentUser.id;
                              await loadAttendanceRecords(userId, currentUser.isAdmin);
                            } catch (error) {
                              console.error('삭제 오류:', error);
                              alert('기록 삭제에 실패했습니다.');
                            }
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-lg text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* 주휴수당 정보 */}
          {salary.eligibleWeeks && salary.eligibleWeeks.length > 0 && (
            <div className="mt-6 bg-blue-50 rounded-xl p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                주휴수당 적용 주
              </h3>
              <div className="space-y-3">
                {salary.eligibleWeeks.map((week, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          {new Date(week.weekStart).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })} ~ 
                          {new Date(week.weekEnd).toLocaleDateString('ko-KR', { month: 'long', day: 'numeric' })}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          근무일: {week.dates.map(d => new Date(d).getDate()).join(', ')}일 | 
                          총 {week.totalHours.toFixed(1)}시간 근무
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          주휴수당 ({week.allowanceHours.toFixed(1)}시간)
                        </p>
                        <p className="text-lg font-bold text-blue-600">
                          +{week.allowanceAmount.toLocaleString()}원
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          ({week.totalHours.toFixed(1)}/40) × 8 × {hourlyWage.toLocaleString()}원
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-blue-200">
                <p className="text-sm text-blue-700">
                  * 주 15시간 이상 근무 시 8시간분의 주휴수당이 지급됩니다
                </p>
              </div>
            </div>
          )}
        </div>

        {/* 급여 요약 */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-2xl p-8 text-white" style={{ animation: 'fadeIn 0.8s ease-out' }}>
          <h2 className="text-2xl font-bold mb-6 flex items-center">
            <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            이번 달 급여 요약
          </h2>
          <div className="space-y-4">
            <div className="bg-white bg-opacity-20 backdrop-blur rounded-xl p-6">
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-white border-opacity-30">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    총 근무시간
                  </span>
                  <span className="font-bold text-lg">{salary.totalHours.toFixed(1)}시간</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white border-opacity-30">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    기본급 (시급 {hourlyWage.toLocaleString()}원)
                  </span>
                  <span className="font-bold text-lg">{salary.basicPay.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white border-opacity-30">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    주휴수당 ({salary.weeklyAllowanceHours}시간)
                  </span>
                  <span className="font-bold text-lg">{salary.weeklyAllowance.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-white border-opacity-30">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    총급여
                  </span>
                  <span className="font-bold text-lg">{salary.totalPay.toLocaleString()}원</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    소득세 (3.3%)
                  </span>
                  <span className="font-bold text-lg text-red-300">-{Math.floor(salary.tax).toLocaleString()}원</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 transform hover:scale-105 transition-transform duration-200">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-gray-600 text-sm mb-1">실지급액</p>
                  <p className="text-3xl font-bold text-gray-800">
                    {Math.floor(salary.netPay).toLocaleString()}
                    <span className="text-xl font-normal">원</span>
                  </p>
                </div>
                <div className="text-right">
                  <svg className="w-16 h-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* 급여명세서 버튼 */}
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  printWindow.document.write(`
                    <html>
                      <head>
                        <title>급여명세서 - ${currentUser.name} ${selectedYear}년 ${selectedMonth + 1}월</title>
                        <style>
                          @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@300;400;500;600;700;900&display=swap');
                          @page {
                            size: A4;
                            margin: 0;
                          }
                          @media print {
                            body { margin: 0; }
                            .pay-slip { 
                              box-shadow: none !important;
                              padding: 30px !important;
                              page-break-inside: avoid;
                            }
                          }
                          * { 
                            font-family: 'Noto Sans KR', sans-serif;
                            box-sizing: border-box;
                            margin: 0;
                            padding: 0;
                          }
                          body { 
                            margin: 0; 
                            padding: 15px; 
                            background: white;
                            color: #1a1a1a;
                            line-height: 1.5;
                          }
                          .pay-slip { 
                            max-width: 794px; 
                            min-height: 1000px;
                            margin: 0 auto;
                            background: white;
                            box-shadow: 0 0 20px rgba(0,0,0,0.1);
                            padding: 30px;
                          }
                          
                          /* 헤더 스타일 */
                          .header-container {
                            border-bottom: 3px solid #1a1a1a;
                            padding-bottom: 15px;
                            margin-bottom: 20px;
                          }
                          .header-top {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-start;
                            margin-bottom: 20px;
                          }
                          .company-info {
                            text-align: left;
                          }
                          .company-name {
                            font-size: 24px;
                            font-weight: 900;
                            color: #1a1a1a;
                            letter-spacing: -0.5px;
                            margin-bottom: 5px;
                          }
                          .company-details {
                            font-size: 11px;
                            color: #666;
                            line-height: 1.4;
                          }
                          .document-title {
                            text-align: right;
                          }
                          .title {
                            font-size: 32px;
                            font-weight: 900;
                            color: #1a1a1a;
                            letter-spacing: 2px;
                            margin: 0;
                          }
                          .subtitle {
                            font-size: 12px;
                            color: #666;
                            margin-top: 5px;
                            font-weight: 400;
                          }
                          
                          /* 정보 섹션 */
                          .info-section {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 20px;
                            margin-bottom: 20px;
                            background: #f8f9fa;
                            padding: 20px;
                            border: 1px solid #e9ecef;
                          }
                          .info-box {
                            padding: 0;
                          }
                          .info-row {
                            display: flex;
                            margin-bottom: 8px;
                            font-size: 14px;
                          }
                          .info-label {
                            font-weight: 600;
                            color: #495057;
                            width: 80px;
                            flex-shrink: 0;
                          }
                          .info-value {
                            color: #1a1a1a;
                            font-weight: 500;
                          }
                          
                          /* 문서번호 스타일 */
                          .doc-number {
                            text-align: right;
                          }
                          .doc-label {
                            font-size: 12px;
                            color: #666;
                            margin-bottom: 4px;
                          }
                          .doc-value {
                            font-size: 14px;
                            font-weight: 600;
                            color: #1a1a1a;
                          }
                          
                          /* 테이블 스타일 */
                          .section-title {
                            font-size: 18px;
                            font-weight: 700;
                            color: #1a1a1a;
                            margin-bottom: 15px;
                            padding-bottom: 8px;
                            border-bottom: 2px solid #1a1a1a;
                          }
                          .table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                            border: 1px solid #dee2e6;
                          }
                          .table th {
                            background: #212529;
                            color: white;
                            padding: 8px;
                            text-align: center;
                            font-size: 12px;
                            font-weight: 600;
                            border: 1px solid #212529;
                          }
                          .table td {
                            padding: 6px;
                            text-align: center;
                            font-size: 12px;
                            border: 1px solid #dee2e6;
                            background: white;
                          }
                          .table tr:nth-child(even) td {
                            background: #f8f9fa;
                          }
                          
                          /* 요약 섹션 */
                          .summary-container {
                            display: grid;
                            grid-template-columns: 1fr 1fr;
                            gap: 30px;
                            margin-bottom: 30px;
                          }
                          .summary-box {
                            border: 2px solid #dee2e6;
                            padding: 20px;
                            background: #ffffff;
                          }
                          .summary-box-title {
                            font-size: 16px;
                            font-weight: 700;
                            color: #1a1a1a;
                            margin-bottom: 15px;
                            padding-bottom: 10px;
                            border-bottom: 1px solid #dee2e6;
                          }
                          .summary-item {
                            display: flex;
                            justify-content: space-between;
                            padding: 8px 0;
                            font-size: 14px;
                          }
                          .summary-label {
                            color: #495057;
                            font-weight: 500;
                          }
                          .summary-value {
                            color: #1a1a1a;
                            font-weight: 600;
                            text-align: right;
                          }
                          
                          /* 급여 요약 테이블 */
                          .summary-table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-bottom: 20px;
                            border: 1px solid #dee2e6;
                          }
                          .summary-table th {
                            background: #1a1a1a;
                            color: white;
                            padding: 12px;
                            font-weight: 600;
                            text-align: center;
                            border: 1px solid #dee2e6;
                          }
                          .summary-table td {
                            padding: 12px;
                            border: 1px solid #dee2e6;
                            text-align: right;
                          }
                          .summary-table .deduction {
                            color: #d32f2f;
                          }
                          .summary-table .total {
                            font-weight: 700;
                            font-size: 16px;
                          }
                          .summary-footer {
                            background: #f8f9fa;
                          }
                          
                          /* 계산 상세 */
                          .calculation-details {
                            background: #f8f9fa;
                            padding: 15px;
                            border-radius: 4px;
                            margin-bottom: 20px;
                          }
                          .detail-row {
                            display: flex;
                            justify-content: space-between;
                            padding: 6px 0;
                            border-bottom: 1px solid #e9ecef;
                          }
                          .detail-row:last-child {
                            border-bottom: none;
                          }
                          .detail-label {
                            font-size: 14px;
                            color: #666;
                          }
                          .detail-value {
                            font-size: 14px;
                            font-weight: 600;
                            color: #1a1a1a;
                          }
                          
                          /* 총액 섹션 */
                          .total-section {
                            background: #1a1a1a;
                            color: white;
                            padding: 20px;
                            margin-bottom: 30px;
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                          }
                          .total-label {
                            font-size: 20px;
                            font-weight: 700;
                            letter-spacing: 1px;
                          }
                          .total-amount {
                            font-size: 32px;
                            font-weight: 900;
                            letter-spacing: -0.5px;
                          }
                          
                          /* 푸터 */
                          .footer {
                            text-align: center;
                            margin-top: 40px;
                            padding-top: 30px;
                            border-top: 1px solid #dee2e6;
                          }
                          .footer-text {
                            font-size: 14px;
                            color: #495057;
                            margin-bottom: 20px;
                          }
                          .company-stamp {
                            font-size: 20px;
                            font-weight: 900;
                            color: #1a1a1a;
                            margin-top: 20px;
                            letter-spacing: 2px;
                          }
                          .print-info {
                            position: absolute;
                            top: 20px;
                            right: 20px;
                            font-size: 11px;
                            color: #adb5bd;
                          }
                          @media print {
                            @page { 
                              size: A4; 
                              margin: 10mm;
                            }
                            body { 
                              margin: 0; 
                              padding: 0;
                              -webkit-print-color-adjust: exact;
                              print-color-adjust: exact;
                            }
                            .pay-slip { max-width: 100%; page-break-inside: avoid; }
                            .table { page-break-inside: avoid; }
                            .summary-section { page-break-inside: avoid; }
                            /* 브라우저 인쇄 헤더/푸터 숨기기 */
                            @page :first {
                              margin-top: 0;
                            }
                          }
                        </style>
                      </head>
                      <body>
                        <div class="pay-slip">
                          <div class="header-container">
                            <div class="header-top">
                              <div class="company-info">
                                <div class="company-name">(주)에이오엠</div>
                                <div class="company-details">
                                  서울특별시 강남구 자곡로 7길 24<br>
                                  대표이사: 이아영 | 사업자등록번호: 269-87-01574
                                </div>
                              </div>
                              <div class="doc-number">
                                <div class="doc-label">문서번호</div>
                                <div class="doc-value">PAY-${selectedYear}${String(selectedMonth + 1).padStart(2, '0')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}</div>
                              </div>
                            </div>
                            <div class="document-title">
                              <div class="title">급여명세서</div>
                              <div class="subtitle">SALARY STATEMENT</div>
                            </div>
                          </div>
                          
                          <div class="info-section">
                            <div class="info-box">
                              <div class="info-row">
                                <span class="info-label">성명</span>
                                <span class="info-value">${currentUser.name}</span>
                              </div>
                              <div class="info-row">
                                <span class="info-label">지급일</span>
                                <span class="info-value">${new Date(selectedYear, selectedMonth + 1, 0).toLocaleDateString('ko-KR')}</span>
                              </div>
                            </div>
                            <div class="info-box">
                              <div class="info-row">
                                <span class="info-label">귀속년월</span>
                                <span class="info-value">${selectedYear}년 ${selectedMonth + 1}월</span>
                              </div>
                            </div>
                          </div>
                          
                          <h3 class="section-title">근무 내역</h3>
                          <table class="table">
                            <thead>
                              <tr>
                                <th>근무일</th>
                                <th>출근시간</th>
                                <th>퇴근시간</th>
                                <th>근무시간</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${monthRecords.map(record => `
                                <tr>
                                  <td>${new Date(record.date).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric', weekday: 'short' })}</td>
                                  <td>${record.checkIn || '-'}</td>
                                  <td>${record.checkOut || '-'}</td>
                                  <td>${record.workHours ? record.workHours.toFixed(1) + '시간' : '-'}</td>
                                </tr>
                              `).join('')}
                            </tbody>
                          </table>
                          
                          <div class="summary-section">
                            <h3 class="section-title">급여 계산 내역</h3>
                            <table class="summary-table">
                              <thead>
                                <tr>
                                  <th>지급항목</th>
                                  <th>금액</th>
                                  <th>공제항목</th>
                                  <th>금액</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td>기본급</td>
                                  <td>${salary.basicPay.toLocaleString()}원</td>
                                  <td>소득세 (3.3%)</td>
                                  <td class="deduction">${Math.floor(salary.tax).toLocaleString()}원</td>
                                </tr>
                                <tr>
                                  <td>주휴수당</td>
                                  <td>${salary.weeklyAllowance.toLocaleString()}원</td>
                                  <td></td>
                                  <td></td>
                                </tr>
                                <tr class="summary-footer">
                                  <td>지급합계</td>
                                  <td class="total">${salary.totalPay.toLocaleString()}원</td>
                                  <td>공제합계</td>
                                  <td class="total deduction">${Math.floor(salary.tax).toLocaleString()}원</td>
                                </tr>
                              </tbody>
                            </table>
                            <div class="calculation-details">
                              <div class="detail-row">
                                <span class="detail-label">총 근무시간</span>
                                <span class="detail-value">${salary.totalHours.toFixed(1)}시간</span>
                              </div>
                              <div class="detail-row">
                                <span class="detail-label">시급</span>
                                <span class="detail-value">${hourlyWage.toLocaleString()}원</span>
                              </div>
                              <div class="detail-row">
                                <span class="detail-label">주휴수당 적용시간</span>
                                <span class="detail-value">${salary.weeklyAllowanceHours.toFixed(1)}시간</span>
                              </div>
                            </div>
                          </div>
                          
                          ${salary.eligibleWeeks.length > 0 ? `
                            <div class="week-section">
                              <h3 class="section-title">주휴수당 적용 내역</h3>
                              <table class="table">
                                <thead>
                                  <tr>
                                    <th>주간 기간</th>
                                    <th>근무시간</th>
                                    <th>적용시간</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  ${salary.eligibleWeeks.map(week => `
                                    <tr>
                                      <td>${new Date(week.weekStart).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })} ~ ${new Date(week.weekEnd).toLocaleDateString('ko-KR', { month: 'numeric', day: 'numeric' })}</td>
                                      <td>${week.totalHours.toFixed(1)}시간</td>
                                      <td>${week.allowanceHours.toFixed(1)}시간</td>
                                    </tr>
                                  `).join('')}
                                </tbody>
                              </table>
                            </div>
                          ` : ''}
                          
                          <div class="total-section">
                            <div style="font-size: 20px;">실지급액</div>
                            <div class="total-amount">${Math.floor(salary.netPay).toLocaleString()}원</div>
                          </div>
                          
                          <div class="footer">
                            <p>위 금액을 ${selectedYear}년 ${selectedMonth + 1}월분 급여로 지급합니다.</p>
                            <p style="margin-top: 20px; font-weight: bold;">(주)에이오엠</p>
                            <p style="margin-top: 5px; font-size: 12px; color: #666;">대표이사 이아영</p>
                          </div>
                        </div>
                      </body>
                    </html>
                  `);
                  printWindow.document.close();
                  printWindow.onload = function() {
                    // 인쇄 다이얼로그에서 헤더/푸터 제거 안내
                    setTimeout(() => {
                      printWindow.print();
                      // 인쇄 후 창 닫기
                      setTimeout(() => {
                        printWindow.close();
                      }, 1000);
                    }, 500);
                  };
                }}
                className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transform transition-all duration-200 hover:scale-105 shadow-lg font-medium flex items-center gap-3 mx-auto"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                급여명세서 출력
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
